import { EmptyNavPageContainer } from '../components/layout/EmptyNavPageContainer/EmptyNavPageContainer'
import { GetServerSideProps, NextPage } from 'next'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PrismicPreviewData } from '@dtx-company/true-common/src/types/prismic'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { SignupPageContainer } from '../containers/authentication/SignupPageContainer/SignupPageContainer'
import { SignupPageDocument } from '@dtx-company/prismic-codegen'
import { SignupProps } from '../containers/authentication/SignupPageContainer/SignupPageContainer.types'
import { TrialsEnabledFallbackDataProvider } from '@app/common/src/hooks/useTrialsEnabledFallbackData/TrialsEnabledFallbackDataProvider'
import { UnwrapPromise } from 'next/dist/lib/coalesced-function'
import { buildServerSideProps } from '@app/common/src/utils/buildServerSideProps'
import { deserializePlanTypeQueryParam } from '@dtx-company/true-common/src/query-string-serialization/planType/deserializePlanTypeQueryParam'
import { getInviteState } from '../utils/invite'
import { getTrialsEnabledFallbackData } from '@app/common/src/hooks/useTrialsEnabledFallbackData/useTrialsEnabledFallbackData'
import { prismicClient } from '../shared/prismic/src/utils'
import { removeF2RedirectCookie } from '../utils/auth'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCohort } from '@dtx-company/inter-app/src/hooks/cohorts/useCohort'
import { useEffect } from 'react'
import { useRedirectOnAuthChanges } from '@dtx-company/inter-app/src/hooks/auth/useRedirectOnAuthChanges'
import Head from 'next/head'
import events from '@dtx-company/inter-app/src/event-tracking/events/cohorts'

function useTrackPageView(isOnSignupPage: boolean): void {
  const { isAuthChecked } = useAuthState()
  const { name: cohortName } = useCohort()
  useEffect(() => {
    if (isOnSignupPage && isAuthChecked) {
      events.Revenue_SignupPage_Viewed_Page(cohortName)
    }
  }, [cohortName, isOnSignupPage, isAuthChecked])
}

const SignupPage: NextPage<
  {
    fromFlowpage: boolean
    trialsEnabledFallbackData: UnwrapPromise<ReturnType<typeof getTrialsEnabledFallbackData>>
  } & Pick<SignupProps, 'prismicData' | 'planTypeQueryParams' | 'inviteState' | 'planContent'>
> = ({
  fromFlowpage,
  prismicData,
  trialsEnabledFallbackData,
  planTypeQueryParams,
  inviteState,
  planContent
}) => {
  useTrackPageView(true)
  useRedirectOnAuthChanges()

  return (
    <TrialsEnabledFallbackDataProvider data={trialsEnabledFallbackData}>
      <EmptyNavPageContainer page={Routes.SIGN_UP}>
        {prismicData?.background?.url && (
          <Head>
            <link rel="preload" as="image" href={prismicData.background.url} />
          </Head>
        )}
        <SignupPageContainer
          fromFlowpage={fromFlowpage}
          prismicData={prismicData}
          planTypeQueryParams={planTypeQueryParams}
          inviteState={inviteState}
          planContent={planContent}
        />
      </EmptyNavPageContainer>
    </TrialsEnabledFallbackDataProvider>
  )
}

function getPlanContent({
  prismicData,
  planTypeQueryParams
}: {
  prismicData: SignupPageDocument['data']
  planTypeQueryParams: string | undefined
}): SignupProps['planContent'] {
  switch (planTypeQueryParams) {
    case PlanType.PRO_FLEX:
      return prismicData?.growth
    case PlanType.PRO:
      return prismicData?.pro
    case PlanType.PRO_PLUS_ORG:
      return prismicData?.proplus
    default:
      return prismicData?.default
  }
}

export const getServerSideProps: GetServerSideProps = buildServerSideProps(async context => {
  // Determine if we are coming from flowpage or not so we can render the
  // correct text on the server-side (avoiding a hydration issue)
  const fromFlowpage = context.query.flowpage === 'true'
  const ctxPlanType = context.query.planType
  const planTypeQueryParams = ctxPlanType
    ? deserializePlanTypeQueryParam(ctxPlanType.toString())
    : ''
  const ref = (context.previewData as PrismicPreviewData)?.ref ?? ''
  const inviteState = getInviteState(context)

  const [prismicResponse, trialsEnabledFallbackData] = await Promise.all([
    prismicClient.getSingle<SignupPageDocument>('signup_page', {
      ref
    }),
    getTrialsEnabledFallbackData()
  ])

  const planContent = getPlanContent({ prismicData: prismicResponse?.data, planTypeQueryParams })

  if (!prismicResponse?.data) {
    return {
      notFound: true
    }
  }

  removeF2RedirectCookie(context)

  return {
    props: {
      fromFlowpage,
      prismicData: prismicResponse.data,
      trialsEnabledFallbackData,
      planTypeQueryParams,
      inviteState,
      planContent
    }
  }
})

export default SignupPage
